import axios from 'axios';
import { Config } from 'config';
import { getAccessToken } from 'lib/Helper/Auth';
import {
  CandidateLookup,
  CandidateLookupItem,
  CandidateVacanciesSearchFilters,
  CandidateVacanciesSearchResponse,
  CandidateVacancyFeedbackPayload,
  ResultClickPayload,
  SimilarCandidatesSearchFilters,
  VacancyCandidateFeedbackPayload,
  VacancyCandidatesSearchResponse,
  VacancyDefaults,
  VacancyIndexStatus,
  VacancyLookupItem,
} from 'lib/Model/SimilarCandidates';

function getJsonHeaders() {
  return {
    'Content-Type': 'application/json',
    'access-token': getAccessToken(),
  };
}

export function getVacancyDefaults(vacancy: string): Promise<VacancyDefaults> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/similar_candidates/vacancy_defaults`, {
      params: { vacancy },
      responseType: 'json',
      headers: getJsonHeaders(),
    })
    .then((response) => response.data as VacancyDefaults);
}

// recruiter opened a result in the ats. best effort, never in their way
export function logResultClick(
  endpoint: 'vacancy_click' | 'candidate_vacancy_click',
  payload: ResultClickPayload,
): void {
  axios
    .post(
      `${Config.API_BASE_URL}intranet/similar_candidates/${endpoint}`,
      payload,
      { headers: getJsonHeaders() },
    )
    .catch(() => undefined);
}

export function getVacancyIndexStatus(): Promise<VacancyIndexStatus> {
  return axios
    .get(
      `${Config.API_BASE_URL}intranet/similar_candidates/vacancy_index_status`,
      {
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then((response) => response.data as VacancyIndexStatus);
}

// empty q = the vacancies this recruiter searched lately
export function getVacancyLookup(
  q: string,
  includeClosed = false,
): Promise<VacancyLookupItem[]> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/similar_candidates/vacancy_lookup`, {
      params: { q, include_closed: includeClosed },
      responseType: 'json',
      headers: getJsonHeaders(),
    })
    .then((response) => response.data as VacancyLookupItem[]);
}

// empty q = the candidates this recruiter searched lately
export function getCandidateSearchLookup(
  q: string,
): Promise<CandidateLookupItem[]> {
  return axios
    .get(
      `${Config.API_BASE_URL}intranet/similar_candidates/candidate_search_lookup`,
      {
        params: { q },
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then((response) => response.data as CandidateLookupItem[]);
}

export function getCandidateLookup(
  candidate: string,
): Promise<CandidateLookup> {
  return axios
    .get(`${Config.API_BASE_URL}intranet/similar_candidates/candidate_lookup`, {
      params: { candidate },
      responseType: 'json',
      headers: getJsonHeaders(),
    })
    .then((response) => response.data as CandidateLookup);
}

export function searchVacancyCandidates(
  vacancy: string,
  filters: SimilarCandidatesSearchFilters = {},
): Promise<VacancyCandidatesSearchResponse> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/similar_candidates/vacancy_search`,
      { vacancy, ...filters },
      {
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then((response) => response.data as VacancyCandidatesSearchResponse);
}

export function submitVacancyCandidateFeedback(
  payload: VacancyCandidateFeedbackPayload,
): Promise<void> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/similar_candidates/vacancy_feedback`,
      payload,
      {
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then(() => undefined);
}

export function searchCandidateVacancies(
  candidate: string,
  filters: CandidateVacanciesSearchFilters = {},
): Promise<CandidateVacanciesSearchResponse> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/similar_candidates/candidate_vacancy_search`,
      { candidate, ...filters },
      {
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then((response) => response.data as CandidateVacanciesSearchResponse);
}

export function submitCandidateVacancyFeedback(
  payload: CandidateVacancyFeedbackPayload,
): Promise<void> {
  return axios
    .post(
      `${Config.API_BASE_URL}intranet/similar_candidates/candidate_vacancy_feedback`,
      payload,
      {
        responseType: 'json',
        headers: getJsonHeaders(),
      },
    )
    .then(() => undefined);
}
